import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { IndexeddbPersistence } from 'y-indexeddb';
import { debounce } from 'lodash';

/**
 * CollaborationService manages Yjs documents and real-time collaboration
 * Works in both web and Electron environments
 */
class CollaborationService {
    constructor() {
        this.documents = new Map(); // Map of documentId -> YDoc
        this.providers = new Map(); // Map of documentId -> Provider
        this.persistence = new Map(); // Map of documentId -> Persistence
        this.awareness = new Map(); // Map of documentId -> Awareness
        this.isElectron = window.electronAPI !== undefined;
        this.wsUrl = null;
        this.currentUser = null;
        this._initialized = false;
    }

    /**
     * Initialize the collaboration service
     * @param {Object} config - Configuration options
     */
    async initialize(config = {}) {
        const {
            wsUrl = 'ws://localhost:4444',
            user = { id: 'anonymous', name: 'Anonymous User', color: '#000000' }
        } = config;

        this.wsUrl = wsUrl;
        this.currentUser = user;

        // In Electron, get the collaboration server URL from main process
        if (this.isElectron && window.electronAPI.getCollabServerUrl) {
            try {
                this.wsUrl = await window.electronAPI.getCollabServerUrl();
            } catch (error) {
                console.warn('Failed to get collaboration server URL, using default:', error);
            }
        }
        
        this._initialized = true;
    }
    
    /**
     * Check if service is initialized
     * @returns {boolean}
     */
    isInitialized() {
        return this._initialized;
    }

    /**
     * Create or get a Yjs document
     * @param {string} documentId - Unique document identifier
     * @returns {Y.Doc} Yjs document instance
     */
    getDocument(documentId) {
        if (!this.documents.has(documentId)) {
            const doc = new Y.Doc();
            this.documents.set(documentId, doc);
            
            // Set up document structure
            this._initializeDocumentStructure(doc);
        }
        
        return this.documents.get(documentId);
    }

    /**
     * Initialize the document structure with typed shared types
     * @private
     */
    _initializeDocumentStructure(doc) {
        // Claims editor content
        doc.getText('claims');
        
        // Specification editor content
        doc.getXmlFragment('specification');
        
        // Terms data
        doc.getMap('terms');
        
        // Document metadata
        doc.getMap('metadata');
        
        // Comments
        doc.getArray('comments');
        
        // Track changes history
        doc.getArray('changes');
        
        // User presence
        doc.getMap('presence');
    }

    /**
     * Enable real-time collaboration for a document
     * @param {string} documentId - Document to collaborate on
     * @param {Object} options - Collaboration options
     * @returns {Object} Provider and awareness instances
     */
    async enableCollaboration(documentId, options = {}) {
        const doc = this.getDocument(documentId);
        
        // Clean up existing provider if any
        if (this.providers.has(documentId)) {
            await this.disableCollaboration(documentId);
        }

        try {
            // Create WebSocket provider for real-time sync
            const provider = new WebsocketProvider(
                this.wsUrl,
                `patdraft-${documentId}`,
                doc,
                {
                    connect: options.connect !== false,
                    awareness: {
                        user: this.currentUser
                    }
                }
            );

            // Set up awareness for user presence
            const awareness = provider.awareness;
            awareness.setLocalStateField('user', this.currentUser);

            // Store references
            this.providers.set(documentId, provider);
            this.awareness.set(documentId, awareness);

            // Set up event handlers
            this._setupProviderHandlers(provider, documentId);

            return { provider, awareness };
        } catch (error) {
            console.error('Failed to enable collaboration:', error);
            throw error;
        }
    }

    /**
     * Enable offline persistence for a document
     * @param {string} documentId - Document to persist
     * @returns {IndexeddbPersistence} Persistence instance
     */
    async enablePersistence(documentId) {
        const doc = this.getDocument(documentId);
        
        // Clean up existing persistence if any
        if (this.persistence.has(documentId)) {
            const oldPersistence = this.persistence.get(documentId);
            await oldPersistence.destroy();
        }

        try {
            // Create IndexedDB persistence
            const persistence = new IndexeddbPersistence(
                `patdraft-doc-${documentId}`,
                doc
            );

            // Wait for persistence to be ready
            await persistence.whenSynced;

            this.persistence.set(documentId, persistence);
            
            // In Electron, also sync to SQLite
            if (this.isElectron && window.electronAPI.syncDocument) {
                this._setupElectronSync(documentId, doc);
            }
            
            return persistence;
        } catch (error) {
            console.error('Failed to enable persistence:', error);
            throw error;
        }
    }

    /**
     * Set up sync with Electron's SQLite database
     * @private
     */
    _setupElectronSync(documentId, doc) {
        // Debounced sync function
        const syncToSQLite = debounce(async () => {
            try {
                const data = {
                    claims: doc.getText('claims').toString(),
                    specification: doc.getXmlFragment('specification').toString(),
                    terms: doc.getMap('terms').toJSON(),
                    metadata: doc.getMap('metadata').toJSON()
                };
                
                await window.electronAPI.syncDocument(documentId, data);
            } catch (error) {
                console.error('Failed to sync to SQLite:', error);
            }
        }, 2000); // Sync after 2 seconds of inactivity

        // Listen for document updates
        doc.on('update', syncToSQLite);
    }

    /**
     * Disable collaboration for a document
     * @param {string} documentId - Document ID
     */
    async disableCollaboration(documentId) {
        const provider = this.providers.get(documentId);
        if (provider) {
            provider.disconnect();
            provider.destroy();
            this.providers.delete(documentId);
            this.awareness.delete(documentId);
        }
    }

    /**
     * Setup event handlers for provider
     * @private
     */
    _setupProviderHandlers(provider, documentId) {
        provider.on('status', ({ status }) => {
            console.log(`Collaboration status for ${documentId}:`, status);
            
            // Dispatch custom event for UI updates
            window.dispatchEvent(new CustomEvent('collaboration:status', {
                detail: { documentId, status }
            }));
        });

        provider.on('sync', (isSynced) => {
            console.log(`Document ${documentId} sync status:`, isSynced);
            
            window.dispatchEvent(new CustomEvent('collaboration:sync', {
                detail: { documentId, isSynced }
            }));
        });

        // Handle awareness changes (user presence)
        const awareness = provider.awareness;
        awareness.on('change', () => {
            const users = Array.from(awareness.getStates().values())
                .filter(state => state.user)
                .map(state => state.user);
            
            window.dispatchEvent(new CustomEvent('collaboration:users', {
                detail: { documentId, users }
            }));
        });
    }

    /**
     * Get collaboration status for a document
     * @param {string} documentId - Document ID
     * @returns {Object} Status information
     */
    getStatus(documentId) {
        const provider = this.providers.get(documentId);
        const persistence = this.persistence.get(documentId);
        const doc = this.documents.get(documentId);

        return {
            hasDocument: !!doc,
            isCollaborating: !!provider && provider.wsconnected,
            isPersisted: !!persistence,
            users: this.getActiveUsers(documentId)
        };
    }

    /**
     * Get active users for a document
     * @param {string} documentId - Document ID
     * @returns {Array} List of active users
     */
    getActiveUsers(documentId) {
        const awareness = this.awareness.get(documentId);
        if (!awareness) return [];

        return Array.from(awareness.getStates().values())
            .filter(state => state.user)
            .map(state => state.user);
    }

    /**
     * Add a comment to the document
     * @param {string} documentId - Document ID
     * @param {Object} comment - Comment data
     */
    addComment(documentId, comment) {
        const doc = this.getDocument(documentId);
        const comments = doc.getArray('comments');
        
        comments.push([{
            id: `comment-${Date.now()}`,
            userId: this.currentUser.id,
            userName: this.currentUser.name,
            timestamp: new Date().toISOString(),
            ...comment
        }]);
    }

    /**
     * Track a change in the document
     * @param {string} documentId - Document ID
     * @param {Object} change - Change data
     */
    trackChange(documentId, change) {
        const doc = this.getDocument(documentId);
        const changes = doc.getArray('changes');
        
        changes.push([{
            id: `change-${Date.now()}`,
            userId: this.currentUser.id,
            userName: this.currentUser.name,
            timestamp: new Date().toISOString(),
            ...change
        }]);
    }

    /**
     * Clean up all resources
     */
    async destroy() {
        // Disable all collaborations
        for (const documentId of this.providers.keys()) {
            await this.disableCollaboration(documentId);
        }

        // Destroy all persistence
        for (const [_documentId, persistence] of this.persistence.entries()) {
            await persistence.destroy();
        }

        // Clear all maps
        this.documents.clear();
        this.providers.clear();
        this.persistence.clear();
        this.awareness.clear();
    }
}

// Export singleton instance
export const collaborationService = new CollaborationService();

// Expose to window for debugging/development
if (typeof window !== 'undefined') {
    window.collaborationService = collaborationService;
}